import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Loader2, Sparkles, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

import { getBackendUrl, getApiUrl } from '../../config/environment';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * IntakeModal Component - Owen Smart Intake v3
 * Adaptive questionnaire (5-10 questions) for personalized learning paths
 * 
 * Features:
 * - Adaptive questioning based on response clarity
 * - Multiple choice + free text options
 * - AI-powered analysis with GPT-4o
 * - Emotional intelligence scoring
 * - Premium glassmorphism design
 * - Maximum 10 questions
 */
const IntakeModal = ({ isOpen, onClose, onComplete }) => {
  const { user } = useAuth();
  
  // State management
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  const [freeText, setFreeText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(10); // Max 10 questions
  
  // Initialize intake on mount
  useEffect(() => {
    if (isOpen && !sessionId) {
      initializeIntake();
    }
  }, [isOpen]);
  
  /**
   * Initialize intake session - calls POST /api/intake/start-session
   */
  const initializeIntake = async () => {
    try {
      setIsLoading(true);
      console.log('🧠 Owen Smart Intake v3: Starting session...');
      
      const response = await axios.post(
        `${API}/intake/start-session`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('✅ Intake: Session started', response.data);
      
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.first_question);
      setQuestionNumber(1);
      setTotalQuestions(10); // Adaptive max
      
    } catch (error) {
      console.error('❌ Intake: Failed to start', error);
      if (error.response?.status === 403) {
        toast.error('Cette fonctionnalité est réservée aux membres Premium 💎');
      } else {
        toast.error('Owen n\'a pas pu démarrer le questionnaire. Réessaie dans un instant 💜');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Submit answer and get next question or complete - Owen Smart Intake v3
   */
  const submitAnswer = async () => {
    // Validate answer
    const answer = selectedOption || freeText;
    if (!answer || !answer.trim()) {
      toast.error('Merci de répondre à la question avant de continuer');
      return;
    }
    
    try {
      setIsLoading(true);
      console.log(`🧠 Intake: Submitting response ${questionNumber}/10`);
      
      // Submit response to backend
      const response = await axios.post(
        `${API}/intake/submit-response`,
        {
          session_id: sessionId,
          question_id: currentQuestion.id,
          selected_option: selectedOption || null,
          free_text: freeText || null,
          question_number: questionNumber
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('✅ Intake: Response analyzed', response.data);
      
      const { analysis, next_question, is_complete } = response.data;
      
      // Log AI analysis
      console.log('📊 AI Analysis:', {
        clarity: analysis.clarity_score,
        depth: analysis.depth_score,
        emotions: analysis.emotional_indicators,
        needs: analysis.detected_needs
      });
      
      if (is_complete) {
        // Questionnaire complete - generate personalized path
        console.log('✨ Questionnaire terminé ! Génération du parcours...');
        await generatePersonalizedPath();
      } else if (next_question) {
        // Move to next question
        setCurrentQuestion(next_question);
        setQuestionNumber(questionNumber + 1);
        setSelectedOption('');
        setFreeText('');
      } else {
        // Unexpected state
        toast.error('Erreur inattendue. Réessaie.');
      }
      
    } catch (error) {
      console.error('❌ Intake: Failed to submit', error);
      if (error.response?.data?.detail) {
        toast.error(`Erreur: ${error.response.data.detail}`);
      } else {
        toast.error('Owen n\'a pas pu enregistrer ta réponse. Réessaie 💜');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Generate personalized learning path - POST /api/intake/generate-path
   */
  const generatePersonalizedPath = async () => {
    try {
      setIsLoading(true);
      setIsComplete(true);
      
      console.log('🎯 Generating personalized path...');
      
      const response = await axios.post(
        `${API}/intake/generate-path`,
        {
          session_id: sessionId
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('✅ Path generated!', response.data);
      
      // Show success message
      toast.success(`🎉 ${response.data.message}`);
      
      // Wait a moment for user to see completion
      setTimeout(() => {
        onComplete && onComplete();
      }, 2500);
      
    } catch (error) {
      console.error('❌ Failed to generate path', error);
      if (error.response?.data?.detail) {
        toast.error(`Erreur: ${error.response.data.detail}`);
      } else {
        toast.error('Erreur lors de la génération du parcours');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitAnswer();
    }
  };
  
  if (!isOpen) return null;

  // Iter317 — Refonte premium light : palette ivoire/or/violet, halos lumineux,
  // remplace le fond violet sombre. Cohérent avec le design system Iter304-316.
  const T = {
    ink: '#1C1147',
    inkSoft: '#5A5169',
    ivory: '#FAFAF8',
    border: '#E8E4D9',
    goldDeep: '#B7901F',
    gold: '#D4AF37',
    goldLight: '#F3D060',
    violet: '#7A4FCF',
    violetSoft: '#A78BFA',
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 overflow-y-auto overscroll-contain"
        style={{ background: T.ivory }}
        data-testid="intake-modal"
      >
        {/* Iter317 — Halos ambient lumineux (or top-right + violet bottom-left) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <span
            aria-hidden
            className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(212,175,55,0.22) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />
          <span
            aria-hidden
            className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(122,79,207,0.18) 0%, transparent 70%)',
              filter: 'blur(50px)',
            }}
          />
          <span
            aria-hidden
            className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[280px] h-[280px] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(255,252,236,0.6) 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          />
        </div>

        {/* Close button — sticky top-right, safe-area-inset compatible */}
        <motion.button
          onClick={onClose}
          className="fixed z-50 rounded-full flex items-center justify-center transition-all touch-manipulation"
          style={{
            top: 'max(1rem, env(safe-area-inset-top))',
            right: 'max(1rem, env(safe-area-inset-right))',
            width: '48px',
            height: '48px',
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            color: T.ink,
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.7), 0 6px 20px -8px rgba(28,17,71,0.22)',
          }}
          whileHover={{ scale: 1.05, rotate: 90 }}
          whileTap={{ scale: 0.92 }}
          data-testid="intake-modal-close-btn"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </motion.button>

        {/* Main content container */}
        <div
          className="relative z-10 w-full max-w-3xl mx-auto px-5 sm:px-6 py-8 md:py-12 min-h-full flex flex-col justify-center"
          style={{
            paddingTop: 'max(2rem, env(safe-area-inset-top))',
            paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
          }}
        >
          {/* Progress indicator premium */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-7"
          >
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.22em]"
                  style={{ color: T.goldDeep }}
                >
                  Owen · Personnalisation
                </span>
              </div>
              <span
                className="text-xs font-semibold tabular-nums"
                style={{
                  backgroundImage: `linear-gradient(135deg, ${T.goldDeep}, ${T.violet})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {Math.round((questionNumber / totalQuestions) * 100)}% complété
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium" style={{ color: T.inkSoft }}>
                Question {questionNumber} sur {totalQuestions} max
              </span>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ background: 'rgba(232,228,217,0.6)' }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${T.goldDeep}, ${T.gold}, ${T.violet})`,
                  boxShadow: '0 0 12px rgba(212,175,55,0.45)',
                }}
                initial={{ width: 0 }}
                animate={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </motion.div>

          {/* Main card premium */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="rounded-[28px] p-7 sm:p-10 md:p-12 relative overflow-hidden"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.border}`,
              boxShadow:
                '0 1px 0 rgba(255,255,255,0.7) inset, 0 28px 70px -28px rgba(28,17,71,0.25), 0 0 0 1px rgba(212,175,55,0.10)',
            }}
          >
            {/* Loading state */}
            {isLoading && !isComplete && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-12"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                  style={{
                    background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                    boxShadow: '0 12px 28px -12px rgba(212,175,55,0.55)',
                  }}
                >
                  <Sparkles className="w-8 h-8 text-white" />
                </motion.div>
                <p className="text-lg font-medium" style={{ color: T.ink }}>
                  Owen réfléchit…
                </p>
              </motion.div>
            )}

            {/* Completion state */}
            {isComplete && (
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-10"
              >
                {isLoading ? (
                  <>
                    <motion.div
                      animate={{
                        rotate: 360,
                        scale: [1, 1.08, 1],
                      }}
                      transition={{
                        rotate: { duration: 2.4, repeat: Infinity, ease: 'linear' },
                        scale: { duration: 1.5, repeat: Infinity },
                      }}
                      className="w-24 h-24 rounded-full flex items-center justify-center mb-6 relative"
                      style={{
                        background: `linear-gradient(135deg, ${T.goldDeep}, ${T.violet})`,
                        boxShadow: '0 16px 36px -14px rgba(122,79,207,0.55)',
                      }}
                    >
                      <motion.div
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute inset-0 rounded-full"
                        style={{
                          background:
                            'radial-gradient(circle, rgba(212,175,55,0.30) 0%, transparent 70%)',
                          filter: 'blur(16px)',
                        }}
                      />
                      <Sparkles className="w-12 h-12 text-white relative z-10" />
                    </motion.div>
                    <motion.h3
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="text-2xl font-serif font-semibold mb-3 text-center"
                      style={{ color: T.ink, letterSpacing: '-0.01em' }}
                    >
                      Owen analyse vos réponses…
                    </motion.h3>
                    <p className="text-center max-w-md" style={{ color: T.inkSoft }}>
                      Création de votre parcours personnalisé en cours
                    </p>
                    <div className="flex gap-1 mt-6">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          animate={{ y: [0, -10, 0] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: i * 0.2,
                          }}
                          className="w-2 h-2 rounded-full"
                          style={{ background: T.gold }}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.2 }}
                      className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                      style={{
                        background: 'linear-gradient(135deg, #48825A, #6BAE7C)',
                        boxShadow: '0 14px 30px -12px rgba(72,130,90,0.55)',
                      }}
                    >
                      <CheckCircle className="w-12 h-12 text-white" />
                    </motion.div>
                    <h3
                      className="text-3xl font-serif font-semibold mb-3 text-center"
                      style={{ color: T.ink, letterSpacing: '-0.01em' }}
                    >
                      Votre parcours est prêt.
                    </h3>
                    <p className="text-center" style={{ color: T.inkSoft }}>
                      Owen a analysé vos réponses et créé un parcours personnalisé rien que pour vous.
                    </p>
                  </>
                )}
              </motion.div>
            )}

            {/* Question & Answer UI premium */}
            {!isLoading && !isComplete && currentQuestion && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-7"
                key={questionNumber}
              >
                {/* Owen Avatar premium gold */}
                <div className="flex items-start gap-4">
                  <motion.div
                    animate={{
                      scale: [1, 1.04, 1],
                      boxShadow: [
                        '0 12px 28px -12px rgba(212,175,55,0.45)',
                        '0 16px 36px -14px rgba(212,175,55,0.65)',
                        '0 12px 28px -12px rgba(212,175,55,0.45)',
                      ],
                    }}
                    transition={{ duration: 2.4, repeat: Infinity }}
                    className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                    }}
                  >
                    <Sparkles className="w-7 h-7 text-white" />
                  </motion.div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h4 className="font-bold text-base" style={{ color: T.ink }}>
                        Owen
                      </h4>
                      {currentQuestion.is_dynamic && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="text-[10px] px-2.5 py-1 rounded-full flex items-center gap-1 font-bold uppercase tracking-[0.12em]"
                          style={{
                            background: 'rgba(122,79,207,0.10)',
                            color: T.violet,
                            border: '1px solid rgba(122,79,207,0.25)',
                          }}
                        >
                          <Sparkles className="w-3 h-3" />
                          Question personnalisée
                        </motion.span>
                      )}
                    </div>
                    <motion.p
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="font-serif text-xl sm:text-2xl leading-snug"
                      style={{
                        color: T.ink,
                        letterSpacing: '-0.01em',
                        fontWeight: 600,
                      }}
                    >
                      {currentQuestion.question_text}
                    </motion.p>
                  </div>
                </div>

                {/* Answer cards premium */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-2.5"
                >
                  {currentQuestion.options && currentQuestion.options.length > 0 && (
                    <>
                      {currentQuestion.options.map((option, index) => {
                        const isSelected = selectedOption === option;
                        return (
                          <motion.button
                            key={index}
                            onClick={() => {
                              setSelectedOption(option);
                              setFreeText('');
                            }}
                            className="w-full p-4 sm:p-4.5 rounded-2xl text-left transition-all relative overflow-hidden flex items-center justify-between gap-3"
                            style={{
                              background: isSelected
                                ? 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 50%, #FFF4D2 100%)'
                                : '#FFFFFF',
                              border: `1.5px solid ${isSelected ? T.gold : T.border}`,
                              boxShadow: isSelected
                                ? '0 12px 28px -14px rgba(212,175,55,0.45), 0 0 0 1px rgba(212,175,55,0.30)'
                                : '0 1px 0 rgba(28,17,71,0.04)',
                            }}
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.985 }}
                            data-testid={`intake-option-${index}`}
                          >
                            <span
                              className="text-sm sm:text-base font-medium leading-snug"
                              style={{ color: T.ink }}
                            >
                              {option}
                            </span>
                            {isSelected && (
                              <motion.span
                                initial={{ scale: 0, rotate: -90 }}
                                animate={{ scale: 1, rotate: 0 }}
                                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                                style={{
                                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold})`,
                                  boxShadow: '0 6px 14px -6px rgba(212,175,55,0.55)',
                                }}
                              >
                                <CheckCircle className="w-3.5 h-3.5 text-white" />
                              </motion.span>
                            )}
                          </motion.button>
                        );
                      })}
                    </>
                  )}

                  {/* Free text input premium */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="pt-3"
                  >
                    <p
                      className="text-xs font-semibold mb-2 inline-flex items-center gap-1.5"
                      style={{ color: T.inkSoft }}
                    >
                      <Sparkles className="w-3 h-3" style={{ color: T.violet }} />
                      Ou partagez votre propre réponse :
                    </p>
                    <textarea
                      value={freeText}
                      onChange={(e) => {
                        setFreeText(e.target.value);
                        if (e.target.value.trim()) {
                          setSelectedOption('');
                        }
                      }}
                      placeholder="Tapez votre réponse personnalisée ici…"
                      className="w-full min-h-[88px] px-4 py-3 rounded-2xl focus:outline-none transition-all resize-none text-sm sm:text-base"
                      style={{
                        background: '#FFFFFF',
                        border: `1.5px solid ${T.border}`,
                        color: T.ink,
                        fontFamily: 'inherit',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = T.gold;
                        e.currentTarget.style.boxShadow = '0 0 0 4px rgba(212,175,55,0.15)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = T.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      data-testid="intake-free-text"
                    />
                  </motion.div>
                </motion.div>

                {/* Submit button premium gold/violet */}
                <motion.button
                  onClick={submitAnswer}
                  disabled={(!selectedOption && !freeText.trim()) || isLoading}
                  className="w-full py-4 px-6 rounded-full font-bold text-base flex items-center justify-center gap-3 transition-all relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(135deg, ${T.goldDeep} 0%, ${T.gold} 50%, ${T.violet} 100%)`,
                    color: '#FFFFFF',
                    boxShadow:
                      '0 1px 0 rgba(255,255,255,0.30) inset, 0 14px 30px -10px rgba(122,79,207,0.45), 0 10px 24px -10px rgba(212,175,55,0.55)',
                  }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.985 }}
                  data-testid="intake-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Owen analyse…
                    </>
                  ) : (
                    <>
                      Suivant
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </motion.div>
            )}
          </motion.div>

          {/* Info text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center text-xs sm:text-sm mt-6"
            style={{ color: T.inkSoft }}
          >
            Répondez à ces quelques questions pour qu'Owen crée votre parcours sur mesure.
          </motion.p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default IntakeModal;
